import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useReveal } from "./useReveal";
import { fireConfetti } from "./confetti";
import ZipChecker from "./ZipChecker";
import MaterialDirectory from "./MaterialDirectory";
import PhotoUploader, { UploadedPhoto } from "./PhotoUploader";
import { submitNetlifyForm, base64ToBlob } from "./netlifyForms";
import PickupWaitlist from "./PickupWaitlist";
import { BOOKINGS_OPEN } from "./siteMode";
import { track } from "./analytics";
import "./PickupPage.css";

function getMinDateTime() {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
}

const PickupPage: React.FC = () => {
  const [photos, setPhotos] = useState<UploadedPhoto[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pickupDateTime, setPickupDateTime] = useState("");
  const [pickupType, setPickupType] = useState("Electronics / E-Waste");
  const [dateError, setDateError] = useState("");
  const formStarted = React.useRef(false);
  useReveal();

  const markFormStarted = () => {
    if (!formStarted.current) {
      formStarted.current = true;
      track("pickup_form_started");
    }
  };

  useEffect(() => { if (isSubmitted) fireConfetti(); }, [isSubmitted]);

  const handleDateTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Some browsers don't enforce `min` on datetime-local — reject past dates ourselves.
    if (value && value < getMinDateTime()) {
      setDateError("That date has already passed — pick a future date and time. 📅");
      setPickupDateTime(getMinDateTime());
      return;
    }
    setDateError("");
    setPickupDateTime(value);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Final guard: never submit a pickup in the past.
    if (!pickupDateTime || pickupDateTime < getMinDateTime()) {
      setDateError("Please pick a future date and time for your pickup. 📅");
      document.getElementById("pickupDateTime")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData(e.currentTarget);
      const str = (k: string) => String(formData.get(k) || "");

      // Netlify Forms (multipart so the photos upload too). The team's pickup
      // ticket + the customer's confirmation email are sent server-side by
      // netlify/functions/submission-created.js.
      await submitNetlifyForm(
        "pickup",
        {
          "bot-field": str("bot-field"),
          pickupType: str("pickupType"),
          description: str("description"),
          quantity: str("quantity"),
          placeType: str("placeType"),
          floor: str("floor"),
          elevator: str("elevator"),
          notes: str("notes"),
          name: str("name"),
          phone: str("phone"),
          email: str("email"),
          address: str("address"),
          zip: str("zip"),
          pickupDateTime: str("pickupDateTime"),
        },
        photos.map((p, i) => ({
          field: `photo${i + 1}`,
          blob: base64ToBlob(p.payload.data, p.payload.type),
          filename: p.payload.name || `item-${i + 1}.jpg`,
        }))
      );
      setIsSubmitted(true);
      track("pickup_form_completed");
    } catch (error) {
      console.error('Error submitting form:', error);
      track("pickup_form_error");
      alert('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Bookings paused (REACT_APP_SITE_MODE) → waitlist instead of the form.
  if (!BOOKINGS_OPEN) {
    return (
      <div className="ee-pickup">
        <PickupWaitlist />
        <MaterialDirectory />
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="ee-pickup">
        <div className="ee-pickup-success">
          <img
            src={`${process.env.PUBLIC_URL}/earthjr-thumbs.png`}
            alt="Earth Jr. celebrating with two thumbs up"
            style={{ width: 170, height: "auto", margin: "0 auto 6px", display: "block" }}
          />
          <h1>You’re all set!</h1>
          <p className="ee-success-when">
            Your pickup is scheduled for:<br />
            <strong>
              {pickupDateTime ? new Date(pickupDateTime).toLocaleString(undefined, {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
              }) : "(no date selected)"}
            </strong>
          </p>
          <p className="ee-success-note">We’ve got your request! Our team will reach out by email or phone within 1–2 business days to confirm. 🌿</p>

          <div className="ee-success-steps">
            <h2>What happens next</h2>
            <div className="ee-success-step"><span className="n">1</span><div><b>We’ll reach out to confirm 📧</b><span>Expect an email or a call from us within 1–2 business days to lock in your date and time.</span></div></div>
            <div className="ee-success-step"><span className="n">2</span><div><b>Need to change something? 🚐</b><span>Email everythingeconyc@gmail.com with your name and pickup date — we’ll sort it out.</span></div></div>
            <div className="ee-success-step"><span className="n">3</span><div><b>We come to your door 🌱</b><span>Have your items ready and accessible — we handle the rest, free.</span></div></div>
            <div className="ee-success-step"><span className="n">4</span><div><b>Your items get a second life ❤️</b><span>Repaired, donated to a neighbor in need, or responsibly recycled.</span></div></div>
          </div>

          <div className="ee-success-points">
            <span className="big">🎁</span>
            <div>
              <b>+200 EcoPoints earned</b>
              <span>Double points on your first pickup!</span>
            </div>
          </div>

          <div className="ee-success-btns">
            <button onClick={() => setIsSubmitted(false)} className="ee-btn ee-btn-green">Schedule Another Pickup</button>
            <Link to="/#rewards" className="ee-btn ee-btn-ghost" style={{ color: "var(--text)" }}>See My Rewards →</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ee-pickup">
      <div className="ee-pickup-hero">
        <div className="ee-section-label">Schedule a Pickup</div>
        <h1>Book your free pickup</h1>
        <p>Tell us what you’ve got and when. Our team comes right to your door — no cost, ever.</p>
      </div>

      <div style={{ padding: "0 20px" }}>
        <ZipChecker />
      </div>

      <div className="ee-pickup-body">
        <div className="ee-pickup-card">
          <form
            name="pickup"
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            encType="multipart/form-data"
            onSubmit={handleSubmit}
            onFocusCapture={markFormStarted}
          >
            <input type="hidden" name="form-name" value="pickup" />
            <p className="ee-hidden">
              <label>Don't fill this out: <input name="bot-field" /></label>
            </p>

            <div className="ee-form-group">Your items</div>
            <div className="ee-field-row">
              <div className="ee-field">
                <label>What are you donating?</label>
                <select
                  name="pickupType"
                  className="ee-input"
                  value={pickupType}
                  onChange={(e) => setPickupType(e.target.value)}
                >
                  <option>Electronics / E-Waste</option>
                  <option>Clothing &amp; Textiles</option>
                  <option>Junk Removal / Bulk</option>
                  <option>Mixed / Multiple Items</option>
                </select>
              </div>
              <div className="ee-field">
                <label>How many items?</label>
                <select name="quantity" className="ee-input" defaultValue="1–2">
                  <option>1–2</option>
                  <option>3–5</option>
                  <option>6–10</option>
                  <option>10+</option>
                </select>
              </div>
            </div>

            <div className="ee-field">
              <label>Tell us what you have <span className="ee-field-hint">(brands, sizes, condition — the more we know, the smoother the pickup)</span></label>
              <textarea name="description" className="ee-input" rows={3} placeholder="e.g. 2 laptops (one won’t turn on), a 40-inch TV, 3 bags of clothes" />
            </div>

            <PhotoUploader
              photos={photos}
              onChange={setPhotos}
              max={5}
              label="Photos of your items"
              hint="Optional but really helpful — we bring the right van and crew when we can see what you have."
            />

            <div className="ee-form-group">Pickup address &amp; access</div>

            <div className="ee-field-row">
              <div className="ee-field">
                <label>Full Name</label>
                <input type="text" name="name" required className="ee-input" placeholder="Jordan Rivera" />
              </div>
              <div className="ee-field">
                <label>Phone</label>
                <input type="tel" name="phone" required className="ee-input" placeholder="(917) 555-0100" />
              </div>
            </div>

            <div className="ee-field">
              <label>Email</label>
              <input type="email" name="email" required className="ee-input" placeholder="you@email.com" />
            </div>

            <div className="ee-field">
              <label>Pickup Address</label>
              <textarea name="address" required className="ee-input" rows={2} placeholder="123 Main St, Apt 4B, Brooklyn, NY" />
            </div>
            <div className="ee-field-row">
              <div className="ee-field">
                <label>ZIP code</label>
                <input type="text" name="zip" inputMode="numeric" pattern="[0-9]{5}" required className="ee-input" placeholder="11211" />
              </div>
              <div className="ee-field">
                <label>Type of place</label>
                <select name="placeType" className="ee-input" defaultValue="Apartment">
                  <option>Apartment</option>
                  <option>House</option>
                  <option>Office</option>
                  <option>Business / Store</option>
                  <option>Other</option>
                </select>
              </div>
            </div>
            <div className="ee-field-row">
              <div className="ee-field">
                <label>Floor</label>
                <input type="text" name="floor" className="ee-input" placeholder="e.g. 4 (or Ground)" />
              </div>
              <div className="ee-field">
                <label>Elevator?</label>
                <select name="elevator" className="ee-input" defaultValue="">
                  <option value="">Select…</option>
                  <option value="yes">Yes</option>
                  <option value="no">No — stairs only</option>
                  <option value="n/a">Not needed (ground floor / curb)</option>
                </select>
              </div>
            </div>
            <div className="ee-field">
              <label>Anything we should know? <span className="ee-field-hint">(buzzer, doorman, parking, best time, pets…)</span></label>
              <textarea name="notes" className="ee-input" rows={2} placeholder="e.g. Buzzer 4B, narrow staircase, mornings are best" />
            </div>

            <div className="ee-field">
              <label htmlFor="pickupDateTime">Pickup Date &amp; Time</label>
              <input
                type="datetime-local"
                id="pickupDateTime"
                name="pickupDateTime"
                min={getMinDateTime()}
                required
                className="ee-input"
                value={pickupDateTime}
                onChange={handleDateTimeChange}
                aria-invalid={!!dateError}
                aria-describedby={dateError ? "pickup-date-error" : undefined}
              />
              {dateError && (
                <p id="pickup-date-error" className="ee-date-error" role="alert">{dateError}</p>
              )}
            </div>

            <button type="submit" disabled={isSubmitting} className="ee-btn ee-btn-green ee-btn-full">
              {isSubmitting ? 'Submitting…' : '🌿 Schedule My Free Pickup'}
            </button>
            <p className="ee-form-reassure">100% free · No account needed · Email confirmation</p>
          </form>
        </div>

        <aside className="ee-pickup-side">
          <div className="ee-pickup-video-card">
            <video
              src={`${process.env.PUBLIC_URL}/pickup-transform.mp4`}
              poster={`${process.env.PUBLIC_URL}/pickup-transform-poster.jpg`}
              autoPlay
              muted
              loop
              playsInline
            />
            <p>10 seconds. That's the feeling. 📦✨</p>
          </div>
          <h3>What to expect</h3>
          <ul className="ee-pickup-perks">
            <li><span>📅</span> Pick any date &amp; time that works for you</li>
            <li><span>🚐</span> Friendly team comes to your door</li>
            <li><span>🧾</span> Tax-deductible donation receipt available</li>
            <li><span>🎁</span> Earn 100 EcoPoints per pickup</li>
            <li><span>💸</span> 100% free — no hidden fees</li>
          </ul>
          <div className="ee-pickup-help">
            <strong>Questions?</strong>
            <p>Email us at everythingeconyc@gmail.com and we’ll help you out.</p>
          </div>
        </aside>
      </div>

      {/* ── What we take — searchable directory (data: src/data/materials.ts) ── */}
      <MaterialDirectory />
    </div>
  );
};

export default PickupPage;
